import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { useState } from 'react';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import z from 'zod';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Delete from '@mui/icons-material/Delete';

interface AddSendAsActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
  trigger: UseFormTrigger<PolicyFormValues>;
}

export const AddSendAsActionSchema = z.object({
  type: z.literal('Add_Send_As'),
  defaultSendAs: z.string().optional(),
  domainList: z.array(z.string()).min(1, 'A send as address is required.'),
});

export type AddSendAsActionType = z.infer<typeof AddSendAsActionSchema>;

// Type guard to check if an action is AddSendAsActionType
function isAddSendAsAction(
  action: PolicyAction
): action is AddSendAsActionType {
  return action.type === 'Add_Send_As';
}

export default function AddSendAsAction({
  setValue,
  watch,
  control,
  errors,
  trigger,
}: AddSendAsActionProps) {
  const [alreadySelectedError, setAlreadySelectedError] = useState(false);
  const [sendAsDomain, setSendAsDomain] = useState('');
  const actionData = watch('actionData');
  const idx = actionData.map((action) => action.type).indexOf('Add_Send_As');
  const actionAtIndex = actionData[idx];
  if (!isAddSendAsAction(actionAtIndex)) {
    return null;
  }

  const currentAction: AddSendAsActionType = actionAtIndex;
  const error: FieldErrors<AddSendAsActionType> | undefined =
    errors?.actionData?.[idx];

  function removeSelection(selectedSendAs: string) {
    if (currentAction.defaultSendAs === selectedSendAs) {
      setValue(`actionData.${idx}.defaultSendAs`, '');
    }
    const newList = currentAction.domainList!.filter(
      (sendAs) => sendAs !== selectedSendAs
    );
    setValue(`actionData.${idx}.domainList`, [...newList]);
  }

  return (
    <Stack sx={{ p: 1 }}>
      <Stack gap={2}>
        <Stack flexDirection={'row'} gap={1} alignItems={'baseline'}>
          <Typography>user@</Typography>
          <Controller
            name={`actionData.${idx}.domainList`}
            control={control}
            render={() => (
              <TextField
                inputProps={{
                  id: 'sendAs',
                }}
                InputLabelProps={{
                  htmlFor: 'sendAs',
                }}
                label="Domain"
                value={sendAsDomain}
                onChange={(event) => setSendAsDomain(event.target.value)}
                error={alreadySelectedError || !!error?.domainList}
                helperText={
                  alreadySelectedError
                    ? 'Domain is already selected.'
                    : error?.domainList
                    ? error?.domainList?.message
                    : ' '
                }
                sx={{ width: 250 }}
                autoComplete="off"
              />
            )}
          />
          <Box>
            <Button
              sx={{ height: 40 }}
              variant="contained"
              disabled={sendAsDomain.length === 0}
              onClick={() => {
                if (currentAction.domainList.includes(sendAsDomain)) {
                  setAlreadySelectedError(true);
                } else {
                  setAlreadySelectedError(false);
                  setValue(
                    `actionData.${idx}.domainList`,
                    currentAction.domainList
                      ? [...(currentAction.domainList || []), sendAsDomain]
                      : [sendAsDomain]
                  );
                  setSendAsDomain('');
                  trigger(`actionData.${idx}.domainList`);
                }
              }}
            >
              Add
            </Button>
          </Box>
        </Stack>
        <Box sx={{ maxHeight: 210 }}>
          <Paper sx={{ minWidth: 470, overflow: 'hidden', maxHeight: 210 }}>
            <TableContainer sx={{ minWidth: 470, maxHeight: 210 }}>
              <Table size={'small'} stickyHeader aria-label="selected-shares">
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={3} align="left">
                      Send as Domains to Add
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentAction.domainList
                    ? currentAction.domainList.map((domain) => (
                        <TableRow key={domain}>
                          <TableCell>
                            <Typography variant="body2">{domain}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    currentAction.defaultSendAs === domain
                                  }
                                  onChange={() => {
                                    if (
                                      currentAction.defaultSendAs === domain
                                    ) {
                                      setValue(
                                        `actionData.${idx}.defaultSendAs`,
                                        ''
                                      );
                                    } else {
                                      setValue(
                                        `actionData.${idx}.defaultSendAs`,
                                        domain
                                      );
                                    }
                                  }}
                                />
                              }
                              label="Make Default"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip arrow title="Delete">
                              <span>
                                <IconButton
                                  onClick={() => {
                                    removeSelection(domain);
                                  }}
                                >
                                  <Delete />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Stack>
    </Stack>
  );
}
